// Ported from nostro.operations.component.{ts,html}
// Displays pending nostro operations (purchase orders or withdrawal orders)
// with stats widgets, a client-side filter, and confirm/reject actions.
import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import { usePermissions } from '@/auth/usePermissions';
import { AddWithdrawFundsStatusEnum } from '@/types/nostro';
import type { ExternalServicesOperationsRecord } from '@/types/nostro';
import {
  useNostroOperationsQuery,
  useNostroStatsQuery,
  usePaymentIntermediariesQuery,
} from './api';
import { NostroOperationDetailDialog } from './NostroOperationDetailDialog';

function formatXaf(amount: number | undefined): string {
  if (amount == null) return '';
  return `XAF ${amount.toLocaleString('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB'); // dd/MM/yyyy
}

function formatTime(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function getAccountName(record: ExternalServicesOperationsRecord): string {
  if (record.fields['Provider Account Name']) {
    return `${record.fields['Provider Account Name']} ${record.fields['Provider Account Type'] ?? ''}`.trim();
  }
  return '';
}

function statusColor(
  status: string | undefined,
): 'warning' | 'success' | 'error' | 'default' {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'processed':
      return 'success';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
}

export function NostroOperationsPage() {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const { open: openDialog } = useDialog();

  const [filterWithdrawal, setFilterWithdrawal] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [menuState, setMenuState] = useState<{
    anchor: HTMLElement;
    record: ExternalServicesOperationsRecord;
  } | null>(null);

  const rowsPerPage = 10;
  const orderType = filterWithdrawal ? 'WITHDRAWAL_ORDER' : 'PURCHASE_ORDER';

  const statsQuery = useNostroStatsQuery(AddWithdrawFundsStatusEnum.PENDING);
  const recordsQuery = useNostroOperationsQuery({
    orderType,
    status: AddWithdrawFundsStatusEnum.PENDING,
  });
  const intermediariesQuery = usePaymentIntermediariesQuery();

  const records = recordsQuery.data ?? [];

  const filtered = useMemo(() => {
    const sorted = [...records].sort((a, b) => {
      const da = a.fields.Date ?? '';
      const db = b.fields.Date ?? '';
      return da < db ? 1 : da > db ? -1 : 0;
    });
    if (!filter.trim()) return sorted;
    const term = filter.toLowerCase();
    return sorted.filter((r) => {
      const uuid = r.fields.UUID ?? '';
      const accountName = getAccountName(r);
      const status = r.fields.Status ?? '';
      const amount = String(r.fields['Amount to Pay'] ?? '');
      const fee = String(r.fields.Fees ?? '');
      return (
        uuid.toLowerCase().includes(term) ||
        accountName.toLowerCase().includes(term) ||
        status.toLowerCase().includes(term) ||
        amount.toLowerCase().includes(term) ||
        fee.toLowerCase().includes(term)
      );
    });
  }, [records, filter]);

  const paged = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const canAct = hasAny(['edit:ops_confirmUserOp', 'edit:ops_rejectUserOp', 'ADMIN']);

  const handleToggle = (_: React.MouseEvent<HTMLElement>, val: boolean | null) => {
    if (val === null) return; // MUI fires null when deselecting; keep current selection
    setFilterWithdrawal(val);
    setFilter('');
    setPage(0);
  };

  const handleOpenAction = async (
    record: ExternalServicesOperationsRecord,
    actionType: 'confirm' | 'reject',
  ) => {
    setMenuState(null);
    const intermediaries = intermediariesQuery.data ?? [];
    const updated = await openDialog<ExternalServicesOperationsRecord | null>((close) => (
      <NostroOperationDetailDialog
        record={record}
        paymentIntermediaries={intermediaries}
        actionType={actionType}
        onClose={close}
      />
    ));
    if (updated !== null) {
      void Swal.fire({
        position: 'top-end',
        icon: 'success',
        text: t('messages.record-updated-successfully', {
          defaultValue: 'Record updated successfully',
        }),
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const stats = statsQuery.data;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.nostroOperationNavigation', { defaultValue: 'Nostro Operations' })}
      </Typography>

      {/* Stats widgets — permission: view:ops_statsNostroOps */}
      {hasAny(['view:ops_statsNostroOps']) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Widget 1 — Purchase Orders */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {stats?.addFunds?.pending ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pages.nostroOperations.widget1.content', {
                    defaultValue: 'Pending purchase orders',
                  })}
                </Typography>
              </CardContent>
              <CardActionArea
                onClick={() => {
                  setFilterWithdrawal(false);
                  setFilter('');
                  setPage(0);
                }}
                sx={{ bgcolor: 'success.main', px: 2, py: 1 }}
              >
                <Typography variant="body2" color="white">
                  {t('pages.nostroOperations.widget1.title', { defaultValue: 'Purchase Orders' })}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Widget 2 — Withdrawal Orders */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="h4" color="error.main" fontWeight={700}>
                  {stats?.withdrawFunds?.pending ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pages.nostroOperations.widget2.content', {
                    defaultValue: 'Pending withdrawal orders',
                  })}
                </Typography>
              </CardContent>
              <CardActionArea
                onClick={() => {
                  setFilterWithdrawal(true);
                  setFilter('');
                  setPage(0);
                }}
                sx={{ bgcolor: 'error.main', px: 2, py: 1 }}
              >
                <Typography variant="body2" color="white">
                  {t('pages.nostroOperations.widget2.title', {
                    defaultValue: 'Withdrawal Orders',
                  })}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Records table — permission: view:ops_listNostroOps */}
      {hasAny(['view:ops_listNostroOps']) && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Toggle + filter row */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <ToggleButtonGroup
                value={filterWithdrawal}
                exclusive
                onChange={handleToggle}
                size="small"
              >
                <ToggleButton value={false}>
                  {t('pages.nostroOperations.filterPurchaseOrder', {
                    defaultValue: 'Purchase Orders',
                  })}
                </ToggleButton>
                <ToggleButton value={true}>
                  {t('pages.nostroOperations.filterWithdrawFunds', {
                    defaultValue: 'Withdrawal Orders',
                  })}
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField
                size="small"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(0);
                }}
                placeholder={t('pages.nostroOperations.filter', { defaultValue: 'Filter…' })}
                sx={{ minWidth: 220 }}
              />
            </Stack>

            {recordsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t('pages.nostroOperations.table.header.reference', {
                            defaultValue: 'Reference',
                          })}
                        </TableCell>
                        <TableCell>
                          {t('pages.nostroOperations.table.header.accountName', {
                            defaultValue: 'Account name',
                          })}
                        </TableCell>
                        <TableCell>
                          {t('pages.nostroOperations.table.header.createdBy', {
                            defaultValue: 'Created by',
                          })}
                        </TableCell>
                        <TableCell align="center">
                          {t('pages.nostroOperations.table.header.creationDate', {
                            defaultValue: 'Date',
                          })}
                        </TableCell>
                        <TableCell align="center">
                          {t('pages.nostroOperations.table.header.status', {
                            defaultValue: 'Status',
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {t('pages.nostroOperations.table.header.amount', {
                            defaultValue: 'Amount',
                          })}
                        </TableCell>
                        <TableCell align="center">
                          {t('pages.nostroOperations.table.header.actions', {
                            defaultValue: 'Actions',
                          })}
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paged.map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>
                            <Tooltip title={record.fields.UUID ?? ''}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                {record.fields.UUID}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell>{getAccountName(record)}</TableCell>

                          <TableCell>{record.fields['Diool User Name']}</TableCell>

                          <TableCell align="center">
                            <Stack alignItems="center">
                              <Typography variant="body2">
                                {formatDate(record.fields.Date)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(record.fields.Date)}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              label={record.fields.Status}
                              color={statusColor(record.fields.Status)}
                              size="small"
                            />
                          </TableCell>

                          <TableCell align="right">
                            {formatXaf(record.fields['Amount to Pay'])}
                          </TableCell>

                          <TableCell align="center">
                            {record.fields.Status?.toLowerCase() === 'pending' && canAct && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    setMenuState({ anchor: e.currentTarget, record })
                                  }
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>

                                {menuState?.record === record && (
                                  <Menu
                                    anchorEl={menuState.anchor}
                                    open
                                    onClose={() => setMenuState(null)}
                                  >
                                    <MenuItem
                                      onClick={() => void handleOpenAction(record, 'confirm')}
                                    >
                                      <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                                      {t('pages.userOperations.actions.confirm', {
                                        defaultValue: 'Confirm',
                                      })}
                                    </MenuItem>
                                    <MenuItem
                                      onClick={() => void handleOpenAction(record, 'reject')}
                                    >
                                      <CloseIcon fontSize="small" sx={{ mr: 1 }} />
                                      {t('pages.userOperations.actions.reject', {
                                        defaultValue: 'Reject',
                                      })}
                                    </MenuItem>
                                  </Menu>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                      {paged.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 3 }}
                            >
                              {t('messages.no-data', { defaultValue: 'No data' })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  labelDisplayedRows={({ from, to, count }) =>
                    t('pages.userOperations.table.pagination.range', {
                      defaultValue: `${from}–${to} of ${count}`,
                      from,
                      to,
                      count,
                    })
                  }
                />
              </>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
